import axios from "axios";
import { API_BASE_URL } from "../../../constants";

// The game stats API of one CACHE Partner team (backend games views):
//
//   GET    games/?season=2026-27  {team, roster, seasons, season, games, canEdit, refresh}
//   POST   games/                 {game, warnings, refresh}           (201)
//   GET    games/<game_id>/       {game}
//   PUT    games/<game_id>/       {game, warnings, refresh}
//   DELETE games/<game_id>/       {deleted: true, refresh}
//   POST   games/refresh/         {refresh} (runs the score update again)
//
// Admins reach it under admin/cache-teams/<team_id>/games/ (adminGamesUrl);
// a coach page can pass its own base URL later, the calls stay the same.
// A failed call throws an Error whose message is readable as is, with
// `status` (0 when nothing answered), `data` (the server's {error, message,
// fields, lines}) and `code` (data.error, or "timeout" / "network").

export const adminGamesUrl = (teamId) => `${API_BASE_URL}/admin/cache-teams/${encodeURIComponent(teamId || "")}/games/`;

// Longer than the 30 seconds the host lets a request run, so a stalled
// connection gives up instead of holding the sheet busy.
export const REQUEST_TIMEOUT_MS = 45000;

export const TIMED_OUT = "The connection timed out. Try again.";
export const NO_ANSWER = "The server did not answer. Check the connection and try again.";
const GENERIC = "Something went wrong. Try again.";

// The words for an answer that came without a message of its own.
const ERROR_TEXT = {
  invalid: "Fix the highlighted cells.",
  duplicate: "This game is already entered.",
  not_basketball: "Game sheets are for basketball teams only.",
  team_not_found: "This team was not found. It may have been deleted.",
  game_not_found: "This game was not found. It may have been deleted.",
};
const STATUS_TEXT = {
  401: "Your session has ended. Log in again.",
  403: "This account can't enter games for this team.",
  404: "This was not found. It may have been deleted.",
};

// A short plain message from the server (never a gateway's HTML page).
const plainText = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  return text && !text.startsWith("<") && text.length <= 300 ? text : "";
};

// What a failed answer says, in words the page can show.
export const errorText = (status, data) => {
  const d = data && typeof data === "object" ? data : {};
  if (plainText(d.message)) return plainText(d.message);
  if (ERROR_TEXT[d.error]) return ERROR_TEXT[d.error];
  if (STATUS_TEXT[status]) return STATUS_TEXT[status];
  if (status >= 500) return "The server could not finish that. Try again in a moment.";
  return plainText(d.detail) || plainText(d.error) || GENERIC;
};

// An axios failure as the Error the page reads.
export const toError = (error) => {
  if (error?.response) {
    const { status, data } = error.response;
    const body = data && typeof data === "object" ? data : {};
    const err = new Error(errorText(status, body));
    err.status = status;
    err.data = body;
    err.code = typeof body.error === "string" ? body.error : null;
    return err;
  }
  const timedOut = error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";
  const err = new Error(timedOut ? TIMED_OUT : NO_ANSWER);
  err.status = 0;
  err.data = {};
  err.code = timedOut ? "timeout" : "network";
  return err;
};

// The client for one base URL. `token` is the JWT (or a function returning
// it); without one each call reads the signed-in session's token, which an
// admin "open as user" tab keeps per tab.
export const makeGameStatsApi = ({ baseUrl, token } = {}) => {
  const root = String(baseUrl || "").replace(/\/?$/, "/");
  const config = (extra = {}) => {
    const bearer = typeof token === "function" ? token() : token || localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${bearer}` }, timeout: REQUEST_TIMEOUT_MS, ...extra };
  };
  const call = async (request) => {
    try {
      const res = await request();
      return res?.data ?? {};
    } catch (error) {
      throw toError(error);
    }
  };
  const one = (id) => `${root}${encodeURIComponent(id)}/`;
  return {
    list: (season) => call(() => axios.get(root, config(season ? { params: { season } } : {}))),
    get: (id) => call(() => axios.get(one(id), config())),
    create: (body) => call(() => axios.post(root, body, config())),
    update: (id, body) => call(() => axios.put(one(id), body, config())),
    remove: (id) => call(() => axios.delete(one(id), config())),
    refresh: () => call(() => axios.post(`${root}refresh/`, {}, config())),
  };
};
